import is from '@sindresorhus/is';
import type Result from 'true-myth/result';
import { z } from 'zod';

import type { HttpFetcherWithBaseUrl } from '~/shared/http-fetcher';
import type { HttpFetcherError, RequestHeaders } from '~/shared/http-fetcher/http-fetcher';
import {
    REALTIME_HTTP_ERROR_EVENT,
    type RealtimeEventListener,
    type RealtimeEventListenerBuilder,
    type RealtimeHttpError,
} from '~/shared/realtime-event-listener/realtime-event-listener';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import type {
    AgentArtifactDownloadResponse,
    AgentChatMessagePage,
    CallbackRequest,
    ChangeProposalCompareResponse,
    ChatFeedbackRequest,
    ChatStreamEvent,
    CompleteEvent,
    ContextRefreshEnvelope,
    ErrorEvent,
    MessageStartEvent,
    StructuredResponseEvent,
    Thread,
    ThreadList,
    UserMessage,
} from '../../data-contract/chat';
import {
    agentArtifactDownloadResponseSchema,
    changeProposalCompareResponseSchema,
    chatThreadIdSchema,
    completeEventSchema,
    contextRefreshEnvelopeSchema,
    createAgentChatMessagePageSchema,
    errorEventSchema,
    messageStartEventSchema,
    structuredResponseEventSchema,
    threadListSchema,
    threadSchema,
} from '../../data-contract/chat';
import { reportDroppedChatHistoryItem, reportDroppedChatStreamEvent } from '../../data-dog';

const CHAT_BASE = '/v1/agent/chat';

const AG_UI_CHAT_PATH = 'v1/agent/chat/ag-ui';

export type ListThreadMessagesParams = {
    readonly before?: string;
    readonly limit?: number;
};

export type SendMessageParams = {
    readonly body: UserMessage;
    readonly lastEventId?: string;
    readonly threadId: string;
};

/**
 * The server answered the turn request with a non-2xx status instead of a stream, so retrying the
 * same request cannot help. A `429` means another stream still holds the thread's run lock.
 */
export class ChatStreamRejectedError extends Error {
    readonly retryAfterSeconds: number | null;
    readonly status: number;

    constructor({ retryAfterSeconds, status }: RealtimeHttpError) {
        super(`Chat stream rejected with ${status}`);
        this.name = 'ChatStreamRejectedError';
        this.retryAfterSeconds = retryAfterSeconds;
        this.status = status;
    }
}

const realtimeHttpErrorSchema = z.object({
    retryAfterSeconds: z.number().nullable(),
    status: z.number(),
});

export type ChatStreamListener = {
    readonly chunk: (text: string) => void;
    readonly complete: (event: CompleteEvent) => void;
    readonly contextRefresh: (event: ContextRefreshEnvelope) => void;
    readonly error: (event: ErrorEvent | Error) => void;
    readonly messageStart: (event: MessageStartEvent) => void;
    readonly structuredResponse: (event: StructuredResponseEvent) => void;
};

export type ChatStream = {
    readonly start: () => void;
    readonly stop: () => void;
};

export type ChatAgentEndpoint = {
    readonly fetch?: (url: string, init: RequestInit) => Promise<Response>;
    /** Resolved per request, so every run carries a current Authorization header. */
    readonly getRequestHeaders: () => Promise<RequestHeaders>;
    readonly url: string;
};

export type ChatFetchers = {
    readonly chatAgentEndpoint: ChatAgentEndpoint;
    /** Ends the thread's interrupted turn for good, so it is no longer offered for a resume. */
    readonly cancelChatTurn: (threadId: string) => Promise<Result<unknown, HttpFetcherError>>;
    readonly createChatThread: () => Promise<Result<Thread, HttpFetcherError>>;
    readonly downloadAgentArtifact: (
        threadId: string,
        artifactId: string,
    ) => Promise<Result<AgentArtifactDownloadResponse, HttpFetcherError>>;
    readonly deleteChatAttachment: (
        threadId: string,
        attachmentId: string,
    ) => Promise<Result<unknown, HttpFetcherError>>;
    readonly deleteChatThread: (threadId: string) => Promise<Result<unknown, HttpFetcherError>>;
    readonly fetchChangeProposalCompare: (
        blockId: string,
    ) => Promise<Result<ChangeProposalCompareResponse, HttpFetcherError>>;
    readonly fetchChatThread: (
        threadId: string,
        options?: { readonly signal?: AbortSignal },
    ) => Promise<Result<Thread, HttpFetcherError>>;
    readonly fetchChatThreadMessages: (
        threadId: string,
        params?: ListThreadMessagesParams,
        options?: { readonly signal?: AbortSignal },
    ) => Promise<Result<AgentChatMessagePage, HttpFetcherError>>;
    readonly fetchChatThreads: (options?: {
        readonly signal?: AbortSignal;
    }) => Promise<Result<ThreadList, HttpFetcherError>>;
    readonly markChatThreadRead: (threadId: string) => Promise<Result<unknown, HttpFetcherError>>;
    readonly sendChatMessage: (params: SendMessageParams, listeners: Partial<ChatStreamListener>) => ChatStream;
    readonly submitChatFeedback: (payload: ChatFeedbackRequest) => Promise<Result<unknown, HttpFetcherError>>;
    readonly submitChatInteractionCallback: (args: {
        readonly blockId: string;
        readonly eventId: string;
        readonly payload: CallbackRequest;
    }) => Promise<Result<unknown, HttpFetcherError>>;
};

export type ChatFetchersDependencies = {
    readonly realtimeApiFetcher: HttpFetcherWithBaseUrl;
    readonly realtimeBaseUrl: URL;
    readonly realtimeEventListenerBuilder: RealtimeEventListenerBuilder;
    readonly validatingRealtimeApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

type EnvelopeSchemaIssue = {
    readonly code: string;
    readonly path: readonly PropertyKey[];
};

type EnvelopeSchema<T extends ChatStreamEvent> = {
    readonly safeParse: (value: unknown) => {
        readonly data?: T;
        readonly error?: { readonly issues: readonly EnvelopeSchemaIssue[] };
        readonly success: boolean;
    };
};

const extractThreadId = (value: unknown): string | undefined =>
    is.plainObject(value) ? chatThreadIdSchema.safeParse(value['threadId']).data : undefined;

const parseEnvelope = <T extends ChatStreamEvent>(
    schema: EnvelopeSchema<T>,
    raw: string,
    eventType: string,
): T | null => {
    let parsed: unknown;

    try {
        parsed = JSON.parse(raw);
    } catch {
        reportDroppedChatStreamEvent({ eventType });
        return null;
    }

    const result = schema.safeParse(parsed);

    if (result.success === true && !is.undefined(result.data)) {
        return result.data;
    }

    reportDroppedChatStreamEvent({
        eventType,
        issues: result.error?.issues.slice(0, 10).map((issue) => ({ code: issue.code, path: issue.path.join('.') })),
        threadId: extractThreadId(parsed),
    });

    return null;
};

const attachListeners = (listener: RealtimeEventListener, listeners: Partial<ChatStreamListener>): void => {
    listener.addEventListener('message-start', (event: MessageEvent) => {
        if (is.undefined(listeners.messageStart)) return;
        const envelope = parseEnvelope(messageStartEventSchema, event.data, 'message-start');
        if (envelope !== null) listeners.messageStart(envelope);
    });

    listener.addEventListener('chunk', (event: MessageEvent) => {
        if (is.undefined(listeners.chunk)) return;
        listeners.chunk(is.string(event.data) ? event.data : String(event.data));
    });

    listener.addEventListener('structured-response', (event: MessageEvent) => {
        if (is.undefined(listeners.structuredResponse)) return;
        const envelope = parseEnvelope(structuredResponseEventSchema, event.data, 'structured-response');
        if (envelope !== null && envelope.payload.blocks.length > 0) listeners.structuredResponse(envelope);
    });

    listener.addEventListener('context-refresh', (event: MessageEvent) => {
        if (is.undefined(listeners.contextRefresh)) return;
        const envelope = parseEnvelope(contextRefreshEnvelopeSchema, event.data, 'context-refresh');
        if (envelope !== null) listeners.contextRefresh(envelope);
    });

    listener.addEventListener('complete', (event: MessageEvent) => {
        if (is.undefined(listeners.complete)) return;
        const envelope = parseEnvelope(completeEventSchema, event.data, 'complete');
        if (envelope !== null) listeners.complete(envelope);
    });

    listener.addEventListener('error', (event: MessageEvent) => {
        if (is.undefined(listeners.error)) return;
        const envelope = parseEnvelope(errorEventSchema, event.data, 'error');
        if (envelope === null) {
            listeners.error(new Error('Unknown chat stream error'));
            return;
        }
        listeners.error(envelope);
    });

    listener.addEventListener(REALTIME_HTTP_ERROR_EVENT, (event: MessageEvent) => {
        if (is.undefined(listeners.error)) return;
        const parsed = realtimeHttpErrorSchema.safeParse(JSON.parse(String(event.data)));
        if (!parsed.success) return;
        listeners.error(new ChatStreamRejectedError(parsed.data));
    });

    listener.addEventListener('close', () => {
        if (is.undefined(listeners.error)) return;
        listeners.error(new Error('Chat stream closed'));
    });
};

export const createChatFetchers = (dependencies: ChatFetchersDependencies): ChatFetchers => {
    const { realtimeApiFetcher, realtimeBaseUrl, realtimeEventListenerBuilder, validatingRealtimeApiFetcher } =
        dependencies;

    const agentChatMessagePageSchema = createAgentChatMessagePageSchema(reportDroppedChatHistoryItem);

    const fetchChatThreads: ChatFetchers['fetchChatThreads'] = ({ signal } = {}) =>
        validatingRealtimeApiFetcher.get(threadListSchema, {
            pathname: `${CHAT_BASE}/threads`,
            requestOptions: { signal },
        });

    const createChatThread: ChatFetchers['createChatThread'] = () =>
        validatingRealtimeApiFetcher.postJson(threadSchema, {
            pathname: `${CHAT_BASE}/threads`,
            payload: {},
        });

    const fetchChatThread: ChatFetchers['fetchChatThread'] = (threadId, { signal } = {}) =>
        validatingRealtimeApiFetcher.get(threadSchema, {
            pathname: `${CHAT_BASE}/threads/${threadId}`,
            requestOptions: { signal },
        });

    const deleteChatThread: ChatFetchers['deleteChatThread'] = (threadId) =>
        realtimeApiFetcher.delete({
            pathname: `${CHAT_BASE}/threads/${threadId}`,
        });

    const deleteChatAttachment: ChatFetchers['deleteChatAttachment'] = (threadId, attachmentId) =>
        realtimeApiFetcher.delete({
            pathname: `${CHAT_BASE}/threads/${threadId}/attachments/${attachmentId}`,
        });

    const downloadAgentArtifact: ChatFetchers['downloadAgentArtifact'] = (threadId, artifactId) =>
        validatingRealtimeApiFetcher.get(agentArtifactDownloadResponseSchema, {
            pathname: `${CHAT_BASE}/threads/${threadId}/artifacts/${artifactId}/download`,
        });

    const cancelChatTurn: ChatFetchers['cancelChatTurn'] = (threadId) =>
        realtimeApiFetcher.postJson({
            pathname: `${CHAT_BASE}/threads/${threadId}/cancel`,
            payload: {},
        });

    const markChatThreadRead: ChatFetchers['markChatThreadRead'] = (threadId) =>
        realtimeApiFetcher.patchJson({
            pathname: `${CHAT_BASE}/threads/${threadId}/mark-read`,
            payload: {},
        });

    const fetchChatThreadMessages: ChatFetchers['fetchChatThreadMessages'] = (threadId, params, { signal } = {}) => {
        const searchParams: Record<string, number | string> = {};
        if (!is.undefined(params?.limit)) searchParams['limit'] = params.limit;
        if (!is.undefined(params?.before)) searchParams['before'] = params.before;

        return validatingRealtimeApiFetcher.get(agentChatMessagePageSchema, {
            pathname: `${CHAT_BASE}/threads/${threadId}/messages`,
            requestOptions: is.emptyObject(searchParams) ? { signal } : { searchParams, signal },
        });
    };

    const sendChatMessage: ChatFetchers['sendChatMessage'] = (params, listeners) => {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (!is.undefined(params.lastEventId)) {
            headers['Last-Event-ID'] = params.lastEventId;
        }

        const listener = realtimeEventListenerBuilder.build({
            autoReconnect: false,
            method: 'POST',
            path: `${CHAT_BASE}/threads/${params.threadId}/messages`,
            payload: JSON.stringify(params.body),
            requestHeaders: headers,
        });

        attachListeners(listener, listeners);

        return {
            start: () => listener.start(),
            stop: () => listener.stop(),
        };
    };

    const chatAgentEndpoint: ChatAgentEndpoint = {
        getRequestHeaders: () => realtimeEventListenerBuilder.resolveRequestHeaders(),
        url: new URL(`${realtimeBaseUrl.pathname.replace(/\/+$/, '')}/${AG_UI_CHAT_PATH}`, realtimeBaseUrl).toString(),
    };

    const fetchChangeProposalCompare: ChatFetchers['fetchChangeProposalCompare'] = (blockId) =>
        validatingRealtimeApiFetcher.get(changeProposalCompareResponseSchema, {
            pathname: `${CHAT_BASE}/change-proposals/${blockId}`,
        });

    const submitChatInteractionCallback: ChatFetchers['submitChatInteractionCallback'] = ({
        blockId,
        eventId,
        payload,
    }) =>
        realtimeApiFetcher.putJson({
            pathname: `${CHAT_BASE}/interactions/${eventId}/${blockId}`,
            payload,
        });

    const submitChatFeedback: ChatFetchers['submitChatFeedback'] = (payload) =>
        realtimeApiFetcher.postJson({
            pathname: `${CHAT_BASE}/feedback`,
            payload,
        });

    return {
        chatAgentEndpoint,
        cancelChatTurn,
        createChatThread,
        deleteChatAttachment,
        deleteChatThread,
        downloadAgentArtifact,
        fetchChangeProposalCompare,
        fetchChatThread,
        fetchChatThreadMessages,
        fetchChatThreads,
        markChatThreadRead,
        sendChatMessage,
        submitChatFeedback,
        submitChatInteractionCallback,
    };
};
